import React, { useCallback, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { setUser } from "../redux/userSlice";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import { jwtDecode } from "jwt-decode";

// ─────────────────────────────────────────────────────────────────
//  TOKEN VALIDATORS
//  Pure functions — read localStorage, return decoded payload or null.
//  Bad / expired tokens are cleared automatically.
// ─────────────────────────────────────────────────────────────────

function getValidStaffToken() {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    if (decoded.exp && Date.now() / 1000 > decoded.exp) {
      localStorage.removeItem("token");
      return null;
    }
    return decoded;
  } catch {
    localStorage.removeItem("token");
    return null;
  }
}

function getValidPatientToken() {
  const token = localStorage.getItem("patientToken");
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    if (decoded.exp && Date.now() / 1000 > decoded.exp) {
      localStorage.removeItem("patientToken");
      return null;
    }
    if (!decoded.isPatient) {
      localStorage.removeItem("patientToken");
      return null;
    }
    return decoded;
  } catch {
    localStorage.removeItem("patientToken");
    return null;
  }
}

/** Map a decoded staff token to the correct home path */
function staffDashboard(decoded) {
  if (decoded.isAdmin)   return "/admin/home";
  if (decoded.isDoctor)  return "/doctor/home";
  if (decoded.isStaff)   return "/staff/home";
  if (decoded.regDoctor) return "/reg/home";
  return "/home";
}


// ─────────────────────────────────────────────────────────────────
//  1. ProtectedRoute
//     Replaces your existing ProtectedRoute.js entirely.
//     - Fetches the user from the API and stores in Redux (same as
//       your current implementation).
//     - Supports roleType: "admin" | "doctor" | "staff" | "reg" | "user"
//     - No token → /Login
//     - Wrong role → redirects to the user's actual dashboard
// ─────────────────────────────────────────────────────────────────
export function ProtectedRoute({ children, roleType }) {
  const { user } = useSelector((state) => state.user);
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const fetchUser = useCallback(async () => {
    try {
      dispatch(showLoading());
      const res = await axios.post(
        "/api/user/getUser_id",
        { token: localStorage.getItem("token") },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (res.data.success) {
        dispatch(setUser(res.data.data));
      } else {
        localStorage.clear();
        navigate("/Login");
      }
    } catch {
      dispatch(hideLoading());
      localStorage.clear();
      navigate("/Login");
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!user && localStorage.getItem("token")) {
      fetchUser();
    }
  }, [user, fetchUser]);

  // No token at all
  if (!localStorage.getItem("token")) {
    return <Navigate to="/Login" replace />;
  }

  // Still fetching user from API
  if (!user) return null;

  // Role check
  if (roleType) {
    const decoded = jwtDecode(localStorage.getItem("token"));

    if (roleType === "user") {
      if (decoded.isAdmin)   return <Navigate to="/admin/home"  replace />;
      if (decoded.isDoctor)  return <Navigate to="/doctor/home" replace />;
      if (decoded.isStaff)   return <Navigate to="/staff/home"  replace />;
      if (decoded.regDoctor) return <Navigate to="/reg/home"    replace />;
      // Actual user — allow through
      return children;
    }

    const roleMap = {
      admin:  decoded.isAdmin,
      doctor: decoded.isDoctor,
      staff:  decoded.isStaff,
      reg:    decoded.regDoctor,
    };

    if (!roleMap[roleType]) {
      return <Navigate to="/unauthorised" replace />;
    }
  }

  return children;
}


// ─────────────────────────────────────────────────────────────────
//  2. LandingRoute  — for "/"
//     Already logged in → go to dashboard. Not logged in → landing.
// ─────────────────────────────────────────────────────────────────
export function LandingRoute({ children }) {
  const staff = getValidStaffToken();
  if (staff) return <Navigate to={staffDashboard(staff)} replace />;

  const patient = getValidPatientToken();
  if (patient) return <Navigate to="/patient/patientdashboard" replace />;

  return children;
}


// ─────────────────────────────────────────────────────────────────
//  3. StaffPublicRoute  — for /Login, /Register, /reset-password …
//     Replaces your existing PublicRoute.js.
//     Old PublicRoute sent users to "/" which is now the landing —
//     this one sends them to the correct role dashboard instead.
// ─────────────────────────────────────────────────────────────────
export function StaffPublicRoute({ children }) {
  const staff = getValidStaffToken();
  if (staff) return <Navigate to={staffDashboard(staff)} replace />;

  const patient = getValidPatientToken();
  if (patient) return <Navigate to="/patient/patientdashboard" replace />;

  return children;
}


// ─────────────────────────────────────────────────────────────────
//  4. PatientPublicRoute  — for /portal-login
//     Patient already logged in → patient dashboard.
//     Staff already logged in   → staff dashboard.
//     Not logged in             → show patient login page.
// ─────────────────────────────────────────────────────────────────
export function PatientPublicRoute({ children }) {
  const patient = getValidPatientToken();
  if (patient) return <Navigate to="/patient/patientdashboard" replace />;

  const staff = getValidStaffToken();
  if (staff) return <Navigate to={staffDashboard(staff)} replace />;

  return children;
}


// ─────────────────────────────────────────────────────────────────
//  5. PatientProtectedRoute  — for /patient/patientdashboard
//     Valid patientToken → show the page.
//     No / bad token     → /portal-login.
// ─────────────────────────────────────────────────────────────────
export function PatientProtectedRoute({ children }) {
  const patient = getValidPatientToken();
  if (!patient) return <Navigate to="/portal-login" replace />;
  return children;
}